// src/components/forms/BusinessInfoForm.js
import React, { useState } from 'react';
import { validateBusinessInfo } from '../../utils/validation';

const BusinessInfoForm = ({ onSubmit, isSubmitting, userEmail, userName }) => {
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    phone: '',
    address: '',
    business_number: '',
    website: '',

  });
  const [services, setServices] = useState([
    { name: '', duration: '', price: '' }
  ]);
  const [serviceErrors, setServiceErrors] = useState([{}]);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = validateBusinessInfo(formData);

    // Validate services
    const servicesValidation = services.map((service, index) => validateService(service, index));
    const hasServiceErrors = servicesValidation.some(errors => Object.keys(errors).length > 0);

    if (!validation.isValid || hasServiceErrors) {
      setErrors(validation.errors);
      setServiceErrors(servicesValidation);
      return;
    }

    // Submit the data including services
    onSubmit({
      businessName: formData.businessName.trim(),
      businessType: formData.businessType.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      business_number: formData.business_number.trim(),
      website: formData.website.trim(),
      email: userEmail,
      displayName: userName,
      services: services.map(service => ({
        name: service.name.trim(),
        duration: parseInt(service.duration),
        price: parseFloat(service.price)
      }))
    });
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = services.map((service, i) =>
      i === index ? { ...service, [field]: value } : service
    );
    setServices(updatedServices);

    // Clear errors for this field
    if (serviceErrors[index] && serviceErrors[index][field]) {
      const updatedErrors = serviceErrors.map((errorObj, i) =>
        i === index ? { ...errorObj, [field]: '' } : errorObj
      );
      setServiceErrors(updatedErrors);
    }
  };

  const addService = () => {
    setServices([...services, { name: '', duration: '', price: '' }]);
    setServiceErrors([...serviceErrors, {}]);
  };

  const removeService = (index) => {
    if (services.length > 1) {
      const updatedServices = services.filter((_, i) => i !== index);
      const updatedErrors = serviceErrors.filter((_, i) => i !== index);
      setServices(updatedServices);
      setServiceErrors(updatedErrors);
    }
  };


  const validateService = (service, index) => {
    const errors = {};

    if (!service.name || !service.name.trim()) {
      errors.name = 'שם השירות הוא שדה חובה';
    } else if (service.name.trim().length < 2) {
      errors.name = 'שם השירות חייב להכיל לפחות 2 תווים';
    } else if (service.name.trim().length > 50) {
      errors.name = 'שם השירות לא יכול להכיל יותר מ-50 תווים';
    }

    if (!service.duration || !service.duration.toString().trim()) {
      errors.duration = 'משך השירות הוא שדה חובה';
    } else {
      const duration = parseInt(service.duration);
      if (isNaN(duration) || duration <= 0) {
        errors.duration = 'משך השירות חייב להיות מספר חיובי';
      } else if (duration < 15) {
        errors.duration = 'משך השירות חייב להיות לפחות 15 דקות';
      } else if (duration > 480) {
        errors.duration = 'משך השירות לא יכול להיות יותר מ-8 שעות';
      }
    }

    if (!service.price || !service.price.toString().trim()) {
      errors.price = 'מחיר השירות הוא שדה חובה';
    } else {
      const price = parseFloat(service.price);
      if (isNaN(price) || price <= 0) {
        errors.price = 'מחיר השירות חייב להיות מספר חיובי';
      } else if (price > 10000) {
        errors.price = 'מחיר השירות לא יכול להיות יותר מ-10,000 ש"ח';
      }
    }

    return errors;
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* Business Name */}
      <div>
        <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
          שם העסק *
        </label>
        <input
          type="text"
          id="businessName"
          name="businessName"
          value={formData.businessName}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-xl border ${errors.businessName
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-300 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
          placeholder="למשל: סלון יופי דקלה"
          disabled={isSubmitting}
        />
        {errors.businessName && (
          <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
        )}
      </div>

      {/* Business Type */}
      <div>
        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
          סוג העסק *
        </label>
        <input
          type="text"
          id="businessType"
          name="businessType"
          value={formData.businessType}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-xl border ${errors.businessType
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-300 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
          placeholder="למשל: סלון יופי, ספא, קליניקת יופי"
          disabled={isSubmitting}
        />
        {errors.businessType && (
          <p className="mt-1 text-sm text-red-600">{errors.businessType}</p>
        )}
      </div>

      {/* Phone Number */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
          מספר טלפון *
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-xl border ${errors.phone
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-300 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
          placeholder="050-123-4567"
          disabled={isSubmitting}
        />
        {errors.phone && (
          <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
        )}
      </div>

      {/* Address */}
      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
          כתובת העסק *
        </label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          rows={3}
          className={`w-full px-4 py-3 rounded-xl border ${errors.address
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-300 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200 resize-none`}
          placeholder="רחוב, מספר בית, עיר"
          disabled={isSubmitting}
        />
        {errors.address && (
          <p className="mt-1 text-sm text-red-600">{errors.address}</p>
        )}
      </div>

      {/* Business Number */}
      <div>
        <label htmlFor="business_number" className="block text-sm font-medium text-gray-700 mb-2">
          ח"פ (או ת.ז לעוסק זעיר)
        </label>
        <input
          type="text"
          id="business_number"
          name="business_number"
          value={formData.business_number}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-xl border ${errors.business_number
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-300 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
          placeholder="123456789"
          disabled={isSubmitting}
        />
        {errors.business_number && (
          <p className="mt-1 text-sm text-red-600">{errors.business_number}</p>
        )}
      </div>

      {/* Website */}
      <div>
        <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
          אתר אינטרנט (אם יש)
        </label>
        <input
          type="url"
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-xl border ${errors.website
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-300 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
          placeholder="https://www.example.com"
          disabled={isSubmitting}
        />
        {errors.website && (
          <p className="mt-1 text-sm text-red-600">{errors.website}</p>
        )}
      </div>

      {/* Services Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-800">שירותים</h3>
          <span className="text-sm text-purple-600">לפחות שירות אחד נדרש</span>
        </div>

        {services.map((service, index) => (
          <div key={index} className="bg-gray-50 rounded-xl p-6 space-y-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">
                שירות {index + 1}
              </h4>
              {services.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeService(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-200 p-1"
                  disabled={isSubmitting}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Service Name */}
              <div className="md:col-span-1">
                <label htmlFor={`serviceName-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                  שם השירות *
                </label>
                <input
                  type="text"
                  id={`serviceName-${index}`}
                  value={service.name}
                  onChange={(e) => handleServiceChange(index, 'name', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${serviceErrors[index]?.name
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-300 focus:border-purple-500'
                    } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
                  placeholder="למשל: תספורת וסטיילינג"
                  disabled={isSubmitting}
                />
                {serviceErrors[index]?.name && (
                  <p className="mt-1 text-sm text-red-600">{serviceErrors[index].name}</p>
                )}
              </div>

              {/* Duration */}
              <div className="md:col-span-1">
                <label htmlFor={`serviceDuration-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                  משך זמן (דקות) *
                </label>
                <input
                  type="number"
                  id={`serviceDuration-${index}`}
                  value={service.duration}
                  onChange={(e) => handleServiceChange(index, 'duration', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${serviceErrors[index]?.duration
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-300 focus:border-purple-500'
                    } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
                  placeholder="60"
                  min="15"
                  max="480"
                  disabled={isSubmitting}
                />
                {serviceErrors[index]?.duration && (
                  <p className="mt-1 text-sm text-red-600">{serviceErrors[index].duration}</p>
                )}
              </div>

              {/* Price */}
              <div className="md:col-span-1">
                <label htmlFor={`servicePrice-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                  מחיר (ש"ח) *
                </label>
                <input
                  type="number"
                  id={`servicePrice-${index}`}
                  value={service.price}
                  onChange={(e) => handleServiceChange(index, 'price', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border ${serviceErrors[index]?.price
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-300 focus:border-purple-500'
                    } focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200`}
                  placeholder="150"
                  min="0"
                  step="0.01"
                  disabled={isSubmitting}
                />
                {serviceErrors[index]?.price && (
                  <p className="mt-1 text-sm text-red-600">{serviceErrors[index].price}</p>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Add Service Button */}
        <button
          type="button"
          onClick={addService}
          className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting || services.length >= 10}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          הוסף שירות נוסף
          {services.length >= 10 && (
            <span className="text-xs text-gray-500">(מקסימום 10 שירותים)</span>
          )}
        </button>
      </div>

      {/* User Info Display */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">פרטי המשתמש</h3>
        <div className="space-y-1 text-sm text-gray-600">
          <p><span className="font-medium">שם:</span> {userName || 'לא זמין'}</p>
          <p><span className="font-medium">אימייל:</span> {userEmail}</p>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-200 transform ${isSubmitting
          ? 'bg-gray-400 cursor-not-allowed'
          : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 hover:scale-105 focus:ring-4 focus:ring-purple-200'
          } text-white focus:outline-none`}
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            שולח בקשה...
          </div>
        ) : (
          'שלח בקשת גישה'
        )}
      </button>
    </form>
  );
};

export default BusinessInfoForm;